/**
 * components/KelolaSalesModal.js
 * Manage the salespeople list: add new, rename/recode/move-branch, deactivate/reactivate,
 * delete (only when unreferenced by any transaction). Mirrors KelolaKategoriModal's management
 * pattern (add panel + inline edit row + typed-HAPUS delete) — except for the add panel itself,
 * see below.
 *
 * Multi-branch cabang feature: every salesperson now carries a required `branchId`. Rendering
 * splits on the `branchFilter` prop (Penjualan.js's own second-level branch toggle, passed
 * through at open time) — a SPECIFIC branch renders one flat, scoped roster table (as before);
 * "SEMUA" (or no filter) renders one `<h3>` + mini table PER branch, including a branch with
 * zero salespeople (its own empty-state row) — grouped, not a flat list with inline tags.
 *
 * **Stage 3 batch 3b, Part E — a "Pusat" group closes a real, previously-flagged gap.** A role
 * with `branchId: null` (Pusat) used to have no heading to render under here at all — it was
 * simply absent from every group and every scoped filter, invisible in this roster despite being
 * a fully real, working role everywhere else in the app (the picker, a printed document,
 * `salespersonRefCount`). Batch 3a already made a Pusat role CREATABLE (`SalespersonFormModal`'s
 * own branch select includes it); this batch closes the other half by giving it a group here too
 * — reusing the exact same `renderRoster` table every Cabang branch already gets, rendered ONLY
 * when at least one Pusat role exists (so the common case, nobody at Pusat yet, stays exactly as
 * quiet as before). This is NOT the Stage 4 Pusat/Cabang split (no top-level tab/toggle anywhere
 * else changes) — it's the minimal fix for "a role you can create but never see."
 *
 * Person/Role restructure, Stage 2: a role has no name of its own — the roster's Nama column,
 * the edit-row seed, and the delete-confirm message all resolve through the new `persons` prop
 * via `resolveSalespersonName`.
 *
 * **Person/Role restructure, Stage 3 batch 3a — the add panel is GONE, replaced by the shared
 * `SalespersonFormModal`.** "+ Tambah Sales" no longer expands an inline Nama/Kode/Cabang panel
 * in place — creating a role now ALWAYS starts with the same person-search-or-create flow the
 * transaction form's quick-add uses (see that component's own header comment for the full
 * two-stage design and why "ask WHO, then WHERE" can't collapse into one flat form the way this
 * panel used to be). `onAdd` is GONE too — `onCreateSalespersonRole` (App.js's real, atomic
 * create handler) is passed straight through to the nested modal's own `onSubmit`.
 *
 * **The "Ubah Nama" edit row now runs the collision guard before renaming.** If the typed name
 * would make this person's own (unchanged) role-set match a DIFFERENT existing person,
 * `handleSimpan` holds off calling `onUpdate` and shows `PersonCollisionModal` instead — see
 * that component's own header comment for why a rename only ever gets two ways through
 * (continue-with-note, or Batal), never "use the existing person instead." Everything else
 * about this edit row — code/branch fields, their own validation, `onUpdate`'s own
 * `{name, code, branchId}` payload shape — is UNCHANGED; `utils/salespeople.js`'s
 * `updateSalesperson` still renames the linked PERSON, not a (nonexistent) role field.
 *
 * **Stage 4 — the Kode field edits the WHOLE PERSON, not just this one role.** A role has no
 * code of its own anymore (see `utils/persons.js`/`salespeople.js`'s own header comments) — this
 * edit row's Kode field always reads/writes the linked person's ONE code, so saving a new code
 * here changes what EVERY one of that person's roles resolves to at once (a Pusat role plus a
 * Cabang role for the same Budi both become AL the moment either row's Kode is corrected to AL).
 * Uniqueness is checked against every OTHER PERSON's code (`codeUsed`, keyed on
 * `editingRolePersonId`, not the role's own id), and a plain hint under the field says so out
 * loud rather than leaving it a silent surprise. This is the ONLY place a code can still be
 * changed post-creation — the add flow (`SalespersonFormModal`) never asks for one again once a
 * person already has one (see that component's own header comment).
 *
 * **Stage 3 batch 3b, Part C — the note.** Wherever a person is named in this modal (the
 * roster's Nama cell, the orphan section below), their own `note` (from `persons`, via
 * `resolvePersonNote`) renders right after the name in small muted text, absent entirely when
 * blank — the same convention `SalespersonFormModal`/`TransactionDetailModal` now use.
 *
 * **Stage 3 batch 3b, Part D — deactivate/reactivate.** A role's `active` flag (batch 2's own
 * data layer) finally gets UI: every roster row (Pusat, Cabang, active or not) shows a Status
 * cell ("Aktif" plain text, or a muted "Nonaktif" chip) and, in Aksi, a plain
 * "Nonaktifkan"/"Aktifkan" toggle button — a direct, IMMEDIATE `onUpdate(id, {active: !active})`
 * call with NO confirmation dialog at all. This is a deliberate design choice: deactivation is
 * fully REVERSIBLE (the role's id, and every past transaction referencing it, survive untouched
 * — see `utils/salespeople.js`'s own `deactivateSalesperson`/`reactivateSalesperson`), so this
 * must read as an ordinary status change, not as destruction — reusing `DeleteConfirmModal`
 * (typed-HAPUS, "this is permanent" framing) for something reversible would be dishonest in the
 * opposite direction from what that modal exists to communicate. There is no precedent for a
 * reversible toggle anywhere else in this codebase (every other "Aksi" button here is either a
 * plain correction or a guarded permanent delete), so no icon was invented for it either — a
 * plain text button reads as a lighter-weight action than the iconed Ubah/Hapus pair beside it,
 * which is the right visual weight for something this low-stakes. An inactive role's whole `<tr>`
 * is also dimmed (`opacity`) so it reads as "off" at a glance while staying fully visible,
 * editable, and deletable — deactivation never hides a row, only marks it.
 *
 * **Stage 3 batch 3b, Part E — "Orang Tanpa Peran."** A person can end up holding zero roles
 * (their only role was deleted, or they were created and never given one) — `personRefCount ===
 * 0`. Such a person is otherwise invisible everywhere in this app (no picker, no printed
 * document, no roster group references them), so a new section lists every one of them, shown
 * only when non-empty: "+ Tambah Peran" (opens `SalespersonFormModal` pre-skipped straight to
 * step "role", via its own `initialPersonId`/`initialPersonName` props — there is nothing left
 * to search for, the person is already chosen), "Ubah" (a plain inline Nama/Catatan edit,
 * `onUpdatePerson` directly — no collision check is run here, and cannot fire: an orphan person
 * has an EMPTY active role-set, and `detectPersonCollision`'s own subset rule requires BOTH
 * sides non-empty to ever call two people ambiguous — see that function's own header comment),
 * and "Hapus" — a REAL, permanent delete this time (unlike role deactivation above), so this one
 * DOES reuse `DeleteConfirmModal`'s typed-HAPUS gate; always safe to offer here, since
 * `personRefCount === 0` is exactly this section's own membership rule (`deletePerson` itself
 * would no-op otherwise, but there's nothing here that could ever hit that no-op path).
 *
 * **Stage 3 batch 3b, Part F — editing a role's location — SUPERSEDED by Stage 5, below.** The
 * paragraphs that used to live here described a "Pindahkan cabang?" warning shown from THIS
 * edit row before saving a direct `branchId` change on a role with transactions. That mechanism
 * is gone: Stage 5 ("roles pinned to their location") found the underlying premise wrong — a
 * role's transactions are frozen at creation to wherever they actually happened, so letting the
 * role itself keep moving makes its own lifetime transaction COUNT read as a count of wherever
 * it sits TODAY, not what those transactions are actually about. `utils/salespeople.js`'s
 * `updateSalesperson` now REFUSES a direct `branchId` change outright once the role has any
 * transactions — there is nothing left for this edit row's own Cabang field to warn about,
 * because it can no longer perform that edit at all. See the "Stage 5" paragraph below for the
 * replacement.
 *
 * **Stage 5 — roles are pinned to their location.** The edit row's Cabang field is now the ONE
 * clear signal for which of two mechanisms applies to a given role, decided purely by whether
 * `countMap.get(s.id) > 0`:
 *   - **0 transactions** — the field stays exactly what it always was: a live, editable
 *     `<select>` (Pusat option included, via the `PUSAT_VALUE` sentinel), saved by the SAME
 *     Simpan button as Nama/Kode, still running the pre-existing rename/branch-move
 *     `detectPersonCollision` guards below. Its OPTIONS are now filtered through
 *     `pickableBranchesForPerson(..., "edit")` (`utils/salespeople.js`) rather than the raw
 *     `branches` list — Part B's own one-role-per-person-per-location rule means a branch the
 *     SAME person already holds ANY other role at (active or inactive) can never be a valid
 *     destination for a plain field edit, so it's simply never offered rather than silently
 *     refused after Simpan is clicked.
 *   - **Any transactions** — the field becomes a plain read-only branch label plus a hint
 *     pointing at the new "Pindahkan" button in Aksi (below); `editBranchId` can never actually
 *     change through this row for such a role, so the branch-diff/collision logic in
 *     `handleSimpan`/`proceedAfterNameCheck` simply never fires for it — the whole "Pindahkan
 *     cabang?" `ConfirmModal` this file used to render is gone, not merely unreachable.
 *
 * **The "Pindahkan" action (Aksi, shown only when `countMap.get(s.id) > 0`)** opens
 * `MoveSalespersonModal` — a SINGLE dialog that states both halves of the move (the old role
 * being deactivated, what happens at the destination) before its own "Pindahkan" button is even
 * clickable, then calls `onMove(id, newBranchId)` (App.js's `handleMoveSalesperson` →
 * `utils/salespeople.js`'s `moveSalespersonBranch` — deactivate the current role, create-or-
 * reactivate one at the destination, atomically). Its own destination list comes from
 * `pickableBranchesForPerson(..., "move")` with the role's own current branch ALSO excluded
 * (nothing to move TO by picking where it already is) — a branch the person already holds an
 * ACTIVE role at is never offered (refused, not explained); an INACTIVE one is offered, flagged,
 * since picking it reactivates that old role rather than starting fresh. See
 * `MoveSalespersonModal.js`'s own header comment for the full design and the exact wording.
 */
import React, { useMemo, useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { detectPersonCollision, resolvePersonNote } from "../utils/persons";
import { resolveSalespersonName, resolveSalespersonCode, personCollisionMessage, pickableBranchesForPerson } from "../utils/salespeople";
import DeleteConfirmModal from "./DeleteConfirmModal";
import PersonCollisionModal from "./PersonCollisionModal";
import SalespersonFormModal from "./SalespersonFormModal";
import MoveSalespersonModal from "./MoveSalespersonModal";
import { PencilIcon, TrashIcon } from "./Icons";

const CODE_RE = /^[A-Z0-9]{1,4}$/;
const PUSAT_VALUE = "__pusat__"; // <select> sentinel for branchId: null — mirrors SalespersonFormModal's own

export default function KelolaSalesModal({ salespeople, transactions, branches = [], persons = [], branchFilter, onCreateSalespersonRole, onUpdate, onMove, onUpdatePerson, onDeletePerson, onDelete, onClose }) {
  const scoped = !!branchFilter && branchFilter !== "SEMUA";

  const [addOpen, setAddOpen] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  // Holds { result, personId, currentSp, newBranchId } while PersonCollisionModal is open for a
  // rename-in-progress — handleSimpan set this instead of calling onUpdate directly; the
  // dialog's own resolution continues on to the branch-change check below, same as a
  // non-ambiguous rename would have.
  const [renameCollision, setRenameCollision] = useState(null);
  // { result, personId, newBranchId } while the (0-transaction, plain-edit) branch-move
  // collision dialog is open.
  const [branchCollision, setBranchCollision] = useState(null);
  // Stage 5: the salesperson (role) object while MoveSalespersonModal is open for it — the
  // deactivate-and-create/reactivate action for a role WITH transactions.
  const [moveTarget, setMoveTarget] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null); // salesperson object

  // Part E: a person with zero roles at all (active or inactive) — invisible everywhere else in
  // this app, so this modal is where they must stay reachable.
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [personEditName, setPersonEditName] = useState("");
  const [personEditNote, setPersonEditNote] = useState("");
  const [addRoleForPerson, setAddRoleForPerson] = useState(null); // { id, name } while SalespersonFormModal is open for an orphan
  const [deletePersonTarget, setDeletePersonTarget] = useState(null); // person object

  useModalEsc(onClose);

  // Count map: salespersonId -> transaction count (referenced by txn.salespersonId).
  const countMap = useMemo(() => {
    const m = new Map();
    for (const t of transactions) {
      if (!t.salespersonId) continue;
      m.set(t.salespersonId, (m.get(t.salespersonId) || 0) + 1);
    }
    return m;
  }, [transactions]);

  const orphanPersons = useMemo(
    () => persons.filter((p) => !salespeople.some((s) => s.personId === p.id)),
    [persons, salespeople]
  );

  // Stage 4: code uniqueness is a PERSON-level fact now — `excludePersonId`, not a role id, so
  // renaming a role's own linked person's code to its own current value is never a collision.
  const codeUsed = (code, excludePersonId) => persons.some((p) => p.id !== excludePersonId && p.code === code);
  const branchLabelFor = (id) => (id === null ? "Pusat" : (branches.find((b) => b.id === id)?.name || "Cabang"));

  const openAdd = () => { collapseEdit(); setAddOpen(true); };

  // ── Edit panel ────────────────────────────────────────────────────────────
  const openEdit = (s) => {
    setAddOpen(false);
    setEditingId(s.id);
    setEditName(resolveSalespersonName(persons, s) || "");
    setEditCode(resolveSalespersonCode(persons, s) || "");
    setEditBranchId(s.branchId === null ? PUSAT_VALUE : s.branchId);
  };
  const collapseEdit = () => {
    setEditingId(null); setEditName(""); setEditCode(""); setEditBranchId("");
    setRenameCollision(null); setBranchCollision(null);
  };

  // Stage 4: the Kode field in this edit row edits the WHOLE PERSON's code — every sibling role
  // (a Pusat role plus one or more Cabang roles for the same human) shares one code and follows
  // this same edit at once, since none of them store a copy of their own. `editingRolePersonId`
  // (not `editingId`, a role id) is what code-uniqueness excludes below.
  const editingRolePersonId = salespeople.find((s) => s.id === editingId)?.personId;
  const nameEdit = editName.trim();
  const codeEdit = editCode.trim().toUpperCase();
  // Disabled-control sweep (manual-testing report): a blank code used to return "" here too.
  const codeErrorEdit = !codeEdit ? "Wajib diisi." : !CODE_RE.test(codeEdit) ? "Kode harus 1-4 huruf/angka." :
    codeUsed(codeEdit, editingRolePersonId) ? "Kode sudah dipakai." : "";
  const editValid = !!nameEdit && !!codeEdit && !codeErrorEdit && !!editBranchId;

  const finishSimpan = () => {
    const newBranchId = editBranchId === PUSAT_VALUE ? null : editBranchId;
    onUpdate(editingId, { name: nameEdit, code: codeEdit, branchId: newBranchId });
    collapseEdit();
  };

  // Stage 5: this edit row's own Cabang <select> is only ever rendered (and thus editBranchId
  // only ever differs from currentSp.branchId) for a 0-transaction role — a role WITH
  // transactions moves exclusively through the "Pindahkan" action/MoveSalespersonModal below,
  // never through this row. So this collision guard only ever fires for the 0-transaction case,
  // run against a `salespeople` array with the role BEING EDITED filtered out first — otherwise
  // its own stale membership in the computed role-set would produce a false-positive collision
  // against anyone still legitimately at its old branch.
  const proceedAfterNameCheck = (currentSp, newBranchId) => {
    if (newBranchId !== currentSp.branchId) {
      const otherRoles = salespeople.filter((s) => s.id !== currentSp.id);
      const result = detectPersonCollision({ persons, salespeople: otherRoles }, { kind: "add-role", personId: currentSp.personId, branchId: newBranchId });
      if (result.ambiguous) {
        setBranchCollision({ result, personId: currentSp.personId, newBranchId });
        return;
      }
    }
    finishSimpan();
  };

  const handleSimpan = () => {
    if (!editValid) return;
    const currentSp = salespeople.find((s) => s.id === editingId);
    const currentName = resolveSalespersonName(persons, currentSp) || "";
    const newBranchId = editBranchId === PUSAT_VALUE ? null : editBranchId;
    if (nameEdit !== currentName) {
      const result = detectPersonCollision({ persons, salespeople }, { kind: "rename", personId: currentSp.personId, name: nameEdit });
      if (result.ambiguous) {
        setRenameCollision({ result, personId: currentSp.personId, currentSp, newBranchId });
        return; // held — proceedAfterNameCheck runs once the dialog resolves (see the render block)
      }
    }
    proceedAfterNameCheck(currentSp, newBranchId);
  };

  // ── Orphan-person edit panel (Part E) ────────────────────────────────────
  const openPersonEdit = (p) => { setEditingPersonId(p.id); setPersonEditName(p.name); setPersonEditNote(p.note || ""); };
  const collapsePersonEdit = () => { setEditingPersonId(null); setPersonEditName(""); setPersonEditNote(""); };

  // One roster table, reused for both the scoped (one branch) and grouped (per branch, and the
  // Pusat group) cases so the row/edit-row markup never has to be duplicated between them.
  // `table-layout: fixed` + this explicit `<colgroup>` (Stage 3 batch 3b layout fix) — the
  // narrow columns get a real, guaranteed px width, and Nama (no width specified) gets
  // whatever's left, deterministically, regardless of how much the Aksi cluster's 3 buttons or
  // a long note would otherwise have demanded under `table-layout: auto`. See this component's
  // own header comment for why `table-layout: auto` was the actual cause of the overflow this
  // fixes.
  const renderRoster = (roster) => (
    <table className="table kelola-roster-table">
      <colgroup>
        <col />
        <col style={{ width: 70 }} />
        <col style={{ width: 90 }} />
        <col style={{ width: 80 }} />
        <col style={{ width: 290 }} />
      </colgroup>
      <thead><tr><th>Nama</th><th>Kode</th><th>Status</th><th>Transaksi</th><th>Aksi</th></tr></thead>
      <tbody>
        {roster.length === 0 ? (
          <tr><td colSpan={5} className="empty-state">Belum ada sales di cabang ini.</td></tr>
        ) : roster.map((s) => {
          const count = countMap.get(s.id) || 0;
          const isActive = s.active !== false;
          const isEditing = editingId === s.id;
          const note = resolvePersonNote(persons, s.personId);
          return (
            <React.Fragment key={s.id}>
              <tr style={{ cursor: "default", opacity: isActive ? 1 : 0.55 }}>
                <td>
                  <div>{resolveSalespersonName(persons, s) || "—"}</div>
                  {note && <div style={{ color: "var(--muted)", fontSize: 12 }}>— {note}</div>}
                </td>
                <td><span className="chip chip--muted mono">{resolveSalespersonCode(persons, s) || "—"}</span></td>
                <td>{isActive ? "Aktif" : <span className="chip chip--muted">Nonaktif</span>}</td>
                <td>{count}</td>
                <td className="row-actions kelola-sales-actions">
                  <button className="btn btn-sm btn-ghost" onClick={() => (isEditing ? collapseEdit() : openEdit(s))}>
                    <PencilIcon /> Ubah
                  </button>
                  {/* Stage 5: a role with any transactions can never have its branchId edited
                     directly (see this file's own header comment) — "Pindahkan" is the ONE way
                     to relocate it, shown only once there's something to relocate away FROM. */}
                  {count > 0 && (
                    <button className="btn btn-sm btn-ghost" onClick={() => setMoveTarget(s)}>Pindahkan</button>
                  )}
                  <button className="btn btn-sm btn-ghost" onClick={() => onUpdate(s.id, { active: !isActive })}>
                    {isActive ? "Nonaktifkan" : "Aktifkan"}
                  </button>
                  <span className="tip" {...(count > 0 ? { "data-tip": `Dipakai di ${count} transaksi — tidak bisa dihapus.` } : {})}>
                    <button className="btn btn-sm btn-danger-ghost" disabled={count > 0}
                      onClick={() => setDeleteTarget(s)}>
                      <TrashIcon /> Hapus
                    </button>
                  </span>
                </td>
              </tr>
              {isEditing && (
                <tr>
                  <td colSpan={5} style={{ background: "#f8fafc" }}>
                    <div style={{ padding: "10px 4px" }}>
                      <div className="form-row">
                        <label className="form-label" htmlFor={`kelola-sales-edit-nama-${s.id}`}>Nama</label>
                        <input id={`kelola-sales-edit-nama-${s.id}`} className="form-input" value={editName} autoFocus
                          onChange={(e) => setEditName(e.target.value)} />
                        {/* Disabled-control sweep (manual-testing report) — shared convention. */}
                        {!nameEdit && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
                      </div>
                      <div className="form-row">
                        <label className="form-label" htmlFor={`kelola-sales-edit-kode-${s.id}`}>Kode</label>
                        <input id={`kelola-sales-edit-kode-${s.id}`} className="form-input mono" value={editCode}
                          onChange={(e) => setEditCode(e.target.value.toUpperCase())} />
                        {codeErrorEdit
                          ? <div className="form-hint" style={{ color: "var(--red)" }}>{codeErrorEdit}</div>
                          : <div className="form-hint">Kode berlaku untuk semua peran orang ini.</div>}
                      </div>
                      <div className="form-row">
                        <label className="form-label" htmlFor={`kelola-sales-edit-cabang-${s.id}`}>Cabang</label>
                        {/* Stage 5: a 0-transaction role keeps the plain, editable select — the
                           existing simple branch edit; the moment a role holds a transaction,
                           this field becomes read-only and "Pindahkan" (Aksi, above) takes over. */}
                        {count > 0 ? (
                          <>
                            <div>{branchLabelFor(s.branchId)}</div>
                            <div className="form-hint">Peran ini punya transaksi — gunakan tombol "Pindahkan" untuk pindah cabang.</div>
                          </>
                        ) : (
                          <select id={`kelola-sales-edit-cabang-${s.id}`} className="form-select" value={editBranchId} onChange={(e) => setEditBranchId(e.target.value)}>
                            {pickableBranchesForPerson(persons, salespeople, branches, s.personId, s.id, "edit").map((opt) => (
                              <option key={opt.id === null ? PUSAT_VALUE : opt.id} value={opt.id === null ? PUSAT_VALUE : opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="modal-actions" style={{ marginTop: "var(--gap-sm)" }}>
                        <button type="button" className="btn" onClick={collapseEdit}>Batal</button>
                        <button type="button" className="btn btn-primary" disabled={!editValid}
                          onClick={handleSimpan}>Simpan</button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  const activeBranchName = scoped ? (branches.find((b) => b.id === branchFilter)?.name || "—") : null;
  const pusatRoster = salespeople.filter((s) => s.branchId === null);

  return (
    <>
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box modal-box--wide" onMouseDown={(e) => e.stopPropagation()}>
          <h2 className="modal-title">Kelola Sales</h2>
          <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
            Tambah, ubah, nonaktifkan, atau hapus salesperson. Kode dipakai di nomor invoice (cth. 0001/BD/07/2026).
          </div>

          <div style={{ marginBottom: "var(--gap-lg)" }}>
            {/* Part C: Pusat has nothing to do with whether Cabang branches exist — adding a
               Pusat-only person needs no branch at all (SalespersonFormModal's own Cabang
               select already offers Pusat regardless), so this button is never disabled here. */}
            <button type="button" className="btn btn-soft" onClick={openAdd}>
              <span className="btn-circle-plus" aria-hidden="true">+</span> Tambah Sales
            </button>
          </div>

          <div className="table-wrap kelola-table-wrap">
            {scoped ? (
              <>
                <div className="page-sub" style={{ marginBottom: "var(--gap-sm)" }}>Menampilkan sales di cabang {activeBranchName}.</div>
                {renderRoster(salespeople.filter((s) => s.branchId === branchFilter))}
              </>
            ) : (
              <>
                {/* Part C: the Pusat group renders whenever a Pusat role exists, unconditionally
                   of whether any Cabang branch does — it used to be hidden behind the "Belum ada
                   cabang." short-circuit below, making a fully real, working Pusat role
                   invisible in this roster whenever the shop had zero Cabang branches. */}
                {pusatRoster.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h3 style={{ fontSize: 14, marginBottom: "var(--gap-sm)" }}>Pusat</h3>
                    {renderRoster(pusatRoster)}
                  </div>
                )}
                {branches.length === 0 ? (
                  <div className="empty-state">Belum ada cabang.</div>
                ) : (
                  branches.map((b) => (
                    <div key={b.id} style={{ marginBottom: 20 }}>
                      <h3 style={{ fontSize: 14, marginBottom: "var(--gap-sm)" }}>{b.name}</h3>
                      {renderRoster(salespeople.filter((s) => s.branchId === b.id))}
                    </div>
                  ))
                )}
              </>
            )}
          </div>

          {orphanPersons.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3 style={{ fontSize: 14, marginBottom: "var(--gap-xs)" }}>Orang Tanpa Peran</h3>
              <div className="page-sub" style={{ marginBottom: "var(--gap-sm)" }}>
                Belum punya peran (aktif maupun nonaktif) di cabang atau Pusat mana pun.
              </div>
              <div className="table-wrap kelola-table-wrap">
                <table className="table kelola-roster-table">
                  <colgroup>
                    <col />
                    <col style={{ width: 330 }} />
                  </colgroup>
                  <thead><tr><th>Nama</th><th>Aksi</th></tr></thead>
                  <tbody>
                    {orphanPersons.map((p) => {
                      const isEditingPerson = editingPersonId === p.id;
                      return (
                        <React.Fragment key={p.id}>
                          <tr style={{ cursor: "default" }}>
                            <td>
                              <div>{p.name}</div>
                              {p.note && <div style={{ color: "var(--muted)", fontSize: 12 }}>— {p.note}</div>}
                            </td>
                            <td className="row-actions kelola-sales-actions">
                              <button type="button" className="btn btn-sm btn-soft" onClick={() => setAddRoleForPerson({ id: p.id, name: p.name })}>
                                + Tambah Peran
                              </button>
                              <button className="btn btn-sm btn-ghost" onClick={() => (isEditingPerson ? collapsePersonEdit() : openPersonEdit(p))}>
                                <PencilIcon /> Ubah
                              </button>
                              <button className="btn btn-sm btn-danger-ghost" onClick={() => setDeletePersonTarget(p)}>
                                <TrashIcon /> Hapus
                              </button>
                            </td>
                          </tr>
                          {isEditingPerson && (
                            <tr>
                              <td colSpan={2} style={{ background: "#f8fafc" }}>
                                <div style={{ padding: "10px 4px" }}>
                                  <div className="form-row">
                                    <label className="form-label" htmlFor={`kelola-sales-person-nama-${p.id}`}>Nama</label>
                                    <input id={`kelola-sales-person-nama-${p.id}`} className="form-input" value={personEditName} autoFocus
                                      onChange={(e) => setPersonEditName(e.target.value)} />
                                    {/* Disabled-control sweep (manual-testing report) — shared convention. */}
                                    {!personEditName.trim() && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
                                  </div>
                                  <div className="form-row">
                                    <label className="form-label" htmlFor={`kelola-sales-person-catatan-${p.id}`}>Catatan</label>
                                    <textarea id={`kelola-sales-person-catatan-${p.id}`} className="form-input" rows={2} value={personEditNote}
                                      onChange={(e) => setPersonEditNote(e.target.value)} />
                                  </div>
                                  <div className="modal-actions" style={{ marginTop: "var(--gap-sm)" }}>
                                    <button type="button" className="btn" onClick={collapsePersonEdit}>Batal</button>
                                    <button type="button" className="btn btn-primary" disabled={!personEditName.trim()}
                                      onClick={() => { onUpdatePerson(p.id, { name: personEditName, note: personEditNote }); collapsePersonEdit(); }}>
                                      Simpan
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="modal-actions" style={{ marginTop: "var(--gap-lg)" }}>
            <button type="button" className="btn btn-primary" onClick={onClose}>Tutup</button>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <DeleteConfirmModal
          title="Hapus sales?"
          message={<>Anda akan menghapus sales <b>{resolveSalespersonName(persons, deleteTarget) || "—"}</b> ({resolveSalespersonCode(persons, deleteTarget) || "—"}). Tindakan ini permanen.</>}
          requireText="HAPUS"
          onConfirm={() => { onDelete(deleteTarget.id); setDeleteTarget(null); }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {deletePersonTarget && (
        <DeleteConfirmModal
          title="Hapus orang?"
          message={<>Anda akan menghapus <b>{deletePersonTarget.name}</b> dari daftar orang. Tindakan ini permanen.</>}
          requireText="HAPUS"
          onConfirm={() => { onDeletePerson(deletePersonTarget.id); setDeletePersonTarget(null); }}
          onClose={() => setDeletePersonTarget(null)}
        />
      )}

      {addOpen && (
        <SalespersonFormModal
          persons={persons}
          salespeople={salespeople}
          branches={branches}
          initialBranchId={scoped ? branchFilter : undefined}
          onSubmit={onCreateSalespersonRole}
          onClose={() => setAddOpen(false)}
        />
      )}

      {addRoleForPerson && (
        <SalespersonFormModal
          persons={persons}
          salespeople={salespeople}
          branches={branches}
          initialPersonId={addRoleForPerson.id}
          initialPersonName={addRoleForPerson.name}
          onSubmit={onCreateSalespersonRole}
          onClose={() => setAddRoleForPerson(null)}
        />
      )}

      {renameCollision && (
        <PersonCollisionModal
          result={renameCollision.result}
          message={personCollisionMessage("rename", renameCollision.result, branches)}
          canUseExisting={false}
          initialNote={persons.find((p) => p.id === renameCollision.personId)?.note || ""}
          onContinue={(note) => {
            onUpdatePerson(renameCollision.personId, { note });
            const { currentSp, newBranchId } = renameCollision;
            setRenameCollision(null);
            proceedAfterNameCheck(currentSp, newBranchId);
          }}
          onCancel={() => setRenameCollision(null)}
        />
      )}

      {moveTarget && (
        <MoveSalespersonModal
          personName={resolveSalespersonName(persons, moveTarget) || "Peran ini"}
          currentBranchLabel={branchLabelFor(moveTarget.branchId)}
          transactionCount={countMap.get(moveTarget.id) || 0}
          destinations={pickableBranchesForPerson(persons, salespeople, branches, moveTarget.personId, moveTarget.id, "move")
            .filter((d) => d.id !== moveTarget.branchId)}
          onConfirm={(newBranchId) => { onMove(moveTarget.id, newBranchId); setMoveTarget(null); }}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {branchCollision && (
        <PersonCollisionModal
          result={branchCollision.result}
          message={personCollisionMessage("add-role", branchCollision.result, branches, branchLabelFor(branchCollision.newBranchId))}
          canUseExisting={false}
          initialNote={persons.find((p) => p.id === branchCollision.personId)?.note || ""}
          onContinue={(note) => {
            onUpdatePerson(branchCollision.personId, { note });
            setBranchCollision(null);
            finishSimpan();
          }}
          onCancel={() => setBranchCollision(null)}
        />
      )}
    </>
  );
}
